// API-токены партнёра для его собственной системы.
//
// Токен — отдельный токен компании партнёра в Shanyrak: доступ к его
// домофонам и камерам через API, но не тот, которым работает кабинет, —
// отзыв кабинет не трогает. JWT сервер не хранит и в список не отдаёт:
// он приходит при выдаче и по кнопке «Показать» (каждый показ — в журнале).

import { useEffect, useState, type FormEvent } from "react";
import { gql } from "../lib/gql";

type ApiToken = {
  id: string;
  label: string;
  shanyrakTokenUuid: string;
  createdAt: string;
  createdBy: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
};

const TOKEN_FIELDS = `
  id
  label
  shanyrakTokenUuid
  createdAt
  createdBy
  revokedAt
  revokedBy
`;

const LIST_QUERY = `
  query AdminPartnerApiTokens($partnerId: UUID!) {
    adminPartnerApiTokens(partnerId: $partnerId) { ${TOKEN_FIELDS} }
  }
`;

const ISSUE_MUTATION = `
  mutation AdminIssuePartnerApiToken($partnerId: UUID!, $label: String!) {
    adminIssuePartnerApiToken(partnerId: $partnerId, label: $label) {
      token { ${TOKEN_FIELDS} }
      jwt
    }
  }
`;

const REVEAL_MUTATION = `
  mutation AdminRevealPartnerApiToken($id: UUID!) {
    adminRevealPartnerApiToken(id: $id)
  }
`;

const REVOKE_MUTATION = `
  mutation AdminRevokePartnerApiToken($id: UUID!) {
    adminRevokePartnerApiToken(id: $id) { ${TOKEN_FIELDS} }
  }
`;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
}

export function PartnerApiTokensModal({
  partnerId,
  companyName,
  onClose,
}: {
  partnerId: string;
  companyName: string;
  onClose: () => void;
}) {
  const [tokens, setTokens] = useState<ApiToken[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [issuing, setIssuing] = useState(false);
  // Показанные JWT: только что выданный и открытые кнопкой «Показать».
  const [shown, setShown] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function reload() {
    try {
      const data = await gql<{ adminPartnerApiTokens: ApiToken[] }>(LIST_QUERY, { partnerId });
      setTokens(data.adminPartnerApiTokens);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  async function issue(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIssuing(true);
    try {
      const data = await gql<{ adminIssuePartnerApiToken: { token: ApiToken; jwt: string } }>(
        ISSUE_MUTATION,
        { partnerId, label: label.trim() },
      );
      const { token, jwt } = data.adminIssuePartnerApiToken;
      setShown((prev) => ({ ...prev, [token.id]: jwt }));
      setLabel("");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIssuing(false);
    }
  }

  async function reveal(t: ApiToken) {
    setError(null);
    setBusy(t.id);
    try {
      const data = await gql<{ adminRevealPartnerApiToken: string }>(REVEAL_MUTATION, { id: t.id });
      setShown((prev) => ({ ...prev, [t.id]: data.adminRevealPartnerApiToken }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await reload(); // токен мог оказаться отозванным
    } finally {
      setBusy(null);
    }
  }

  async function revoke(t: ApiToken) {
    const ok = window.confirm(
      `Отозвать токен «${t.label}» партнёра ${companyName}?\n\n` +
        `Это необратимо: система партнёра, которая работает с этим токеном, сразу ` +
        `потеряет доступ. Кабинет партнёра это не затронет.`,
    );
    if (!ok) return;
    setError(null);
    setBusy(t.id);
    try {
      await gql(REVOKE_MUTATION, { id: t.id });
      setShown((prev) => {
        const next = { ...prev };
        delete next[t.id];
        return next;
      });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "grid",
        placeItems: "center",
        zIndex: 100,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ maxWidth: 720, width: "100%", maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>API-токены — {companyName}</h3>
          <button className="secondary" onClick={onClose}>
            Закрыть
          </button>
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          Токен для системы партнёра: доступ к его домофонам и камерам через API. Это не
          токен кабинета — отзыв кабинет не затронет. Передавайте токен партнёру только по
          защищённому каналу: с ним можно открывать его двери.
        </p>

        {error && <div className="error-banner">{error}</div>}

        <form className="row" style={{ gap: 8, alignItems: "flex-end", marginBottom: 16 }} onSubmit={issue}>
          <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
            <label>Для чего токен</label>
            <input
              type="text"
              required
              maxLength={100}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Например: система учёта партнёра"
            />
          </div>
          <button type="submit" disabled={issuing || !label.trim()}>
            {issuing ? "Выпускаем…" : "Выпустить токен"}
          </button>
        </form>

        {tokens === null ? (
          <div className="muted">Загружаем…</div>
        ) : tokens.length === 0 ? (
          <div className="muted">Токенов пока нет.</div>
        ) : (
          tokens.map((t) => (
            <TokenRow
              key={t.id}
              token={t}
              jwt={shown[t.id]}
              busy={busy === t.id}
              onReveal={() => reveal(t)}
              onRevoke={() => revoke(t)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TokenRow({
  token,
  jwt,
  busy,
  onReveal,
  onRevoke,
}: {
  token: ApiToken;
  jwt: string | undefined;
  busy: boolean;
  onReveal: () => void;
  onRevoke: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const active = token.revokedAt === null;

  async function copy() {
    if (!jwt) return;
    try {
      await navigator.clipboard.writeText(jwt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Буфер обмена недоступен — токен виден на экране, его можно выделить.
    }
  }

  return (
    <div
      style={{
        borderTop: "1px solid var(--border)",
        padding: "12px 0",
        opacity: active ? 1 : 0.6,
      }}
    >
      <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
        <div>
          <strong>{token.label}</strong>
          <div className="muted" style={{ fontSize: 12 }}>
            Выдан {formatDate(token.createdAt)}
            {token.createdBy ? ` · ${token.createdBy}` : ""}
            {!active && token.revokedAt && (
              <>
                {" "}· отозван {formatDate(token.revokedAt)}
                {token.revokedBy ? ` · ${token.revokedBy}` : ""}
              </>
            )}
          </div>
        </div>
        {active && (
          <div className="row" style={{ gap: 8 }}>
            {!jwt && (
              <button className="secondary" disabled={busy} onClick={onReveal}>
                Показать
              </button>
            )}
            <button className="danger" disabled={busy} onClick={onRevoke}>
              Отозвать
            </button>
          </div>
        )}
      </div>
      {active && jwt && (
        <div className="row" style={{ gap: 8, marginTop: 8, alignItems: "flex-start" }}>
          <code
            style={{
              flex: 1,
              fontFamily: "monospace",
              fontSize: 12,
              wordBreak: "break-all",
              padding: 8,
              background: "var(--bg-elevated)",
              borderRadius: 6,
            }}
          >
            {jwt}
          </code>
          <button onClick={copy}>{copied ? "Скопировано" : "Скопировать"}</button>
        </div>
      )}
    </div>
  );
}
